import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';

import { Box, Card } from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';
import { useToast } from 'src/hooks/useToast';
import { useModal, useConfirmModal } from 'src/hooks/useModal';

import { genericApiToaster } from 'src/services/utils';
import { fetchDepartments } from 'src/services/department.service';
import {
  fetchAllTags,
  deleteMultipleTags,
  fetchTagsByCreatedBy,
} from 'src/services/tag.service';

import TableToolbar from 'src/components/ToolBars/TableToolbar';
import BannerPage from 'src/components/banner-page/banner-page';

import TagsTable from './table/tagsTable';
import AddUpdateTags from './modals/AddUpdateTags';
import SubFilterPopper from './popovers/SubFilterPopover';
import MainFilterPopper from './popovers/MainFilterPopover';

function TagsView() {
  const { t } = useTranslation();
  const { showModal, hideModal } = useModal();
  const { showToast, hideToast } = useToast();
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [page, setPage] = useState(0);
  const [filterName, setFilterName] = useState('');
  const [filterBy, setFilterBy] = useState('id');
  const [searchBy, setSearchBy] = useState('byId');
  const [isLoading, setIsLoading] = useState(false);
  const [filterPopover, setFilterPopover] = useState(null);
  const { role, id } = useAuth();
  const [departmentList, setDepartmentList] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('id');
  const [subFilterPopover, setSubFilterPopover] = useState({
    department: null,
  });

  const getDepartMents = async () => {
    genericApiToaster(
      fetchDepartments,
      [],
      '',
      t('errorMessages.couldntLoadDep'),
      showToast,
      hideToast,
      t,
      setDepartmentList
    );
  };

  useEffect(() => {
    if (role.authority === 'ROLE_ADMIN') getDepartMents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFetchTags = async () => {
    setIsLoading(true);
    genericApiToaster(
      role.authority === 'ROLE_ADMIN' ? fetchAllTags : fetchTagsByCreatedBy,
      [role.authority === 'ROLE_ADMIN' ? '' : id],
      '',
      t('errorMessages.failedToFetchTags'),
      showToast,
      hideToast,
      t,
      setTags,
      undefined,
      () => setIsLoading(false)
    );
  };

  useEffect(() => {
    handleFetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const multipleDeleteSuccessCallback = (res, selected) => {
    showToast({
      message: t('successMessages.deleteMultipleTagsSuccess', {
        tagsDeleted: res.deletedCount,
        totalTags: selected.length || 1,
      }),
      severity: 'success',
      props: { hideToast },
    });
    handleFetchTags();
    setSelectedTags([]);
    setPage(0);
  };

  const multipleDeleteErrorCallback = (err, selected) => {
    if (err.response?.data === 'Tag is related to tickets.') {
      showConfirmMultipleDeleteModal();
    } else {
      showToast({
        message: t('errorMessages.errorDeletingTags'),
        severity: 'error',
        props: { hideToast },
      });
    }
  };

  const multipleTagsDelete = async (selected, isConfirmedDelete = false) => {
    genericApiToaster(
      deleteMultipleTags,
      [selected, isConfirmedDelete],
      '',
      '',
      showToast,
      hideToast,
      t,
      (res) => multipleDeleteSuccessCallback(res, selected),
      (err) => multipleDeleteErrorCallback(err, selected)
    );
  };

  const { showConfirmModal: showConfirmMultipleDeleteModal } = useConfirmModal(
    t('pages.deletePage.confirmDelete'),
    `${t('pages.tagsPage.oneOrMoreTagsRelatedtoTicketsConfirmDeletion')}`,
    async () => {
      multipleTagsDelete(selectedTags, true);
    }
  );

  const handleOpenFilter = (event) => {
    setFilterPopover(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterPopover(null);
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const handleRemoveSelected = () => {
    setSelectedTags([]);
  };

  const handleCloseSubFilter = () => {
    setSubFilterPopover({
      department: null,
    });
  };

  return (
    <Box>
      <BannerPage
        title={t('nav.tags')}
        actionClick={() =>
          showModal({
            title: t('pages.tagsPage.addNewTag'),
            Component: AddUpdateTags,
            props: {
              hideModal,
              handleFetchTags,
              departmentList,
            },
          })
        }
        textButton={t('pages.tagsPage.addNewTag')}
        titleButton={t('pages.tagsPage.addNewTag')}
      />

      <Card>
        <TableToolbar
          numSelected={selectedTags.length}
          filterName={filterName}
          filterBy={searchBy}
          handleCloseFilter={handleCloseFilter}
          handleOpenFilter={handleOpenFilter}
          onFilterName={handleFilterByName}
          selected={selectedTags}
          handleRemoveSelected={handleRemoveSelected}
          deleteFunc={multipleTagsDelete}
          model={t(`nav.tags`)}
          setFilterName={setFilterName}
        />

        <TagsTable
          tags={tags}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          isLoading={isLoading}
          filterName={filterName}
          filterBy={filterBy}
          handleFetchTags={handleFetchTags}
          departmentList={departmentList}
          page={page}
          setPage={setPage}
        />

        {/* main popper */}
        <MainFilterPopper
          filterPopover={filterPopover}
          setFilterName={setFilterName}
          setFilterBy={setFilterBy}
          setSearchBy={setSearchBy}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          handleCloseFilter={handleCloseFilter}
          handleCloseSubFilter={handleCloseSubFilter}
          setSubFilterPopover={setSubFilterPopover}
          setPage={setPage}
        />

        {/* sub popper */}
        <SubFilterPopper
          subFilterPopover={subFilterPopover}
          handleCloseSubFilter={handleCloseSubFilter}
          setFilterBy={setFilterBy}
          setSearchBy={setSearchBy}
          setSelectedFilter={setSelectedFilter}
          setFilterName={setFilterName}
          handleCloseFilter={handleCloseFilter}
          setPage={setPage}
          departmentData={departmentList}
        />
      </Card>
    </Box>
  );
}

export default TagsView;
